import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp, Trash2, Package, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { recipesAPI } from '@/lib/api';
import { formatCurrency, pluralUnit, parseContenanceFromName } from './recipeUtils';

export default function IngredientRow({ ingredient, index, onUpdate, onRemove, onMoveUp, onMoveDown, isFirst, isLast }) {
    const [productSearch, setProductSearch] = useState('');
    const [recipeSearch, setRecipeSearch] = useState('');
    const [productResults, setProductResults] = useState([]);
    const [recipeResults, setRecipeResults] = useState([]);
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [showRecipeDropdown, setShowRecipeDropdown] = useState(false);
    const [showSupplierSelect, setShowSupplierSelect] = useState(false);
    const searchTimeout = useRef(null);
    const productInputRef = useRef(null);
    const recipeInputRef = useRef(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

    const updateDropdownPos = (inputRef) => {
        if (inputRef?.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
        }
    };

    const getPortalTarget = () => document.body;

    useEffect(() => {
        const handler = (e) => {
            if (productInputRef.current && !productInputRef.current.contains(e.target)) {
                const portalDropdown = document.querySelector(`[data-dropdown-for="ingredient-${index}"]`);
                if (portalDropdown && portalDropdown.contains(e.target)) return;
                setShowProductDropdown(false);
            }
            if (recipeInputRef.current && !recipeInputRef.current.contains(e.target)) {
                const portalDropdown = document.querySelector(`[data-dropdown-for="recipe-ingredient-${index}"]`);
                if (portalDropdown && portalDropdown.contains(e.target)) return;
                setShowRecipeDropdown(false);
            }
        };
        const scrollHandler = () => {
            setShowProductDropdown(false);
            setShowRecipeDropdown(false);
        };
        document.addEventListener('mousedown', handler);
        const dialogEl = document.querySelector('[data-testid="recipe-form-dialog"]');
        if (dialogEl) dialogEl.addEventListener('scroll', scrollHandler, true);
        return () => {
            document.removeEventListener('mousedown', handler);
            if (dialogEl) dialogEl.removeEventListener('scroll', scrollHandler, true);
        };
    }, [index]);

    const searchProducts = useCallback((q) => {
        clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(async () => {
            try {
                const res = await recipesAPI.searchIngredients(q, 'product');
                setProductResults(res.data);
                setShowProductDropdown(true);
            } catch { /* ignore */ }
        }, 300);
    }, []);

    const searchRecipes = useCallback((q) => {
        clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(async () => {
            try {
                const res = await recipesAPI.searchIngredients(q, 'recipe');
                setRecipeResults(res.data);
                setShowRecipeDropdown(true);
            } catch { /* ignore */ }
        }, 300);
    }, []);

    const selectProduct = (product) => {
        setShowProductDropdown(false);
        setProductSearch(product.name);
        const supplierProducts = product.supplier_products || [];
        const firstSp = supplierProducts.length === 1 ? supplierProducts[0] : null;
        const purchaseUnit = firstSp ? (firstSp.invoice_unit || firstSp.order_unit || firstSp.billing_unit) : '';
        const baseUnit = product.unit || '';
        onUpdate(index, {
            ...ingredient,
            industrial_product_id: product.id,
            industrial_product_name: product.name,
            industrial_product_image: product.image_url || null,
            base_unit: baseUnit,
            purchase_unit: purchaseUnit,
            unit: purchaseUnit || baseUnit,
            supplier_product_id: firstSp ? firstSp.id : '',
            supplier_product_name: firstSp ? `${firstSp.supplier_name_product} (${firstSp.supplier_name || ''})` : '',
            unit_cost: firstSp ? firstSp.unit_price : 0,
            total_cost: round(ingredient.quantity * (firstSp ? firstSp.unit_price : 0)),
            use_base_unit: false,
            contenance: null,
            _supplierProducts: supplierProducts,
        });
        if (supplierProducts.length > 1) setShowSupplierSelect(true);
    };

    const selectSupplierProduct = (sp) => {
        setShowSupplierSelect(false);
        const purchaseUnit = sp.invoice_unit || sp.order_unit || sp.billing_unit;
        onUpdate(index, {
            ...ingredient,
            supplier_product_id: sp.id,
            supplier_product_name: `${sp.supplier_name_product} (${sp.supplier_name || ''})`,
            purchase_unit: purchaseUnit,
            unit: ingredient.use_base_unit ? ingredient.base_unit : purchaseUnit,
            unit_cost: ingredient.use_base_unit && ingredient.contenance > 0
                ? round(sp.unit_price / ingredient.contenance)
                : sp.unit_price,
            total_cost: ingredient.use_base_unit && ingredient.contenance > 0
                ? round(ingredient.quantity * sp.unit_price / ingredient.contenance)
                : round(ingredient.quantity * sp.unit_price),
            _raw_unit_price: sp.unit_price,
        });
    };

    const selectRecipe = (recipe) => {
        setShowRecipeDropdown(false);
        setRecipeSearch(recipe.name);
        const cpp = recipe.cost_per_portion || 0;
        onUpdate(index, {
            ...ingredient,
            sub_recipe_id: recipe.id,
            sub_recipe_name: recipe.name,
            sub_recipe_image: recipe.image_url || null,
            unit: recipe.yield_unit || 'portion',
            unit_cost: cpp,
            total_cost: round(ingredient.quantity * cpp),
        });
    };

    const handleTypeChange = (type) => {
        onUpdate(index, {
            type,
            industrial_product_id: '',
            supplier_product_id: '',
            sub_recipe_id: '',
            quantity: 0,
            unit: '',
            unit_cost: 0,
            total_cost: 0,
            industrial_product_name: '',
            supplier_product_name: '',
            sub_recipe_name: '',
            use_base_unit: false,
            contenance: null,
            base_unit: '',
            purchase_unit: '',
            _supplierProducts: [],
            _raw_unit_price: 0,
        });
        setProductSearch('');
        setRecipeSearch('');
    };

    const handleUnitModeChange = (useBase) => {
        const rawPrice = ingredient._raw_unit_price || ingredient.unit_cost || 0;
        if (useBase) {
            let autoCont = ingredient.contenance || 0;
            if (!autoCont && ingredient._supplierProducts?.length) {
                const sp = ingredient._supplierProducts.find(s => s.id === ingredient.supplier_product_id);
                if (sp?.capacity_value) autoCont = sp.capacity_value;
            }
            if (!autoCont && ingredient.supplier_product_name) {
                autoCont = parseContenanceFromName(ingredient.supplier_product_name, ingredient.base_unit);
            }
            const ppu = autoCont > 0 ? rawPrice / autoCont : 0;
            onUpdate(index, {
                ...ingredient,
                use_base_unit: true,
                unit: ingredient.base_unit || '',
                unit_cost: round(ppu),
                total_cost: round(ingredient.quantity * ppu),
                contenance: autoCont || null,
                _raw_unit_price: rawPrice,
            });
        } else {
            onUpdate(index, {
                ...ingredient,
                use_base_unit: false,
                unit: ingredient.purchase_unit || '',
                unit_cost: rawPrice,
                total_cost: round(ingredient.quantity * rawPrice),
                _raw_unit_price: rawPrice,
            });
        }
    };

    const round = (v) => Math.round(v * 10000) / 10000;

    return (
        <tr className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors group ingredient-row" data-testid={`ingredient-row-${index}`}>
            {/* Image */}
            <td className="p-2 w-14 ing-cell ing-cell-image" data-mobile-label="">
                {ingredient.type === 'product' ? (
                    ingredient.industrial_product_image ? (
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-slate-200 bg-white">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${ingredient.industrial_product_image}`} alt="" className="w-full h-full object-contain" />
                        </div>
                    ) : ingredient.industrial_product_id ? (
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-slate-50">
                            <Package className="w-5 h-5 text-slate-300" />
                        </div>
                    ) : null
                ) : (
                    ingredient.sub_recipe_image ? (
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border border-orange-200 bg-white">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${ingredient.sub_recipe_image}`} alt="" className="w-full h-full object-contain" />
                        </div>
                    ) : ingredient.sub_recipe_id ? (
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-orange-50">
                            <BookOpen className="w-5 h-5 text-orange-300" />
                        </div>
                    ) : null
                )}
            </td>
            {/* Type */}
            <td className="p-2 w-20 ing-cell" data-mobile-label="Type">
                <Select value={ingredient.type} onValueChange={handleTypeChange}>
                    <SelectTrigger className="h-9 text-sm" data-testid={`ingredient-type-${index}`}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="product">
                            <span className="flex items-center gap-1">Pr</span>
                        </SelectItem>
                        <SelectItem value="recipe">
                            <span className="flex items-center gap-1">S/R</span>
                        </SelectItem>
                    </SelectContent>
                </Select>
            </td>

            {/* Product / Recipe search */}
            <td className="p-2 ing-cell" data-mobile-label="Produit / Sous-recette">
                {ingredient.type === 'product' ? (
                    <div>
                        <div ref={productInputRef}>
                            <Input
                                className="h-9 text-sm"
                                placeholder="Rechercher un produit..."
                                value={ingredient.industrial_product_name || productSearch}
                                onChange={(e) => {
                                    setProductSearch(e.target.value);
                                    onUpdate(index, { ...ingredient, industrial_product_name: '', industrial_product_id: '' });
                                    searchProducts(e.target.value);
                                }}
                                onFocus={() => {
                                    updateDropdownPos(productInputRef);
                                    if (productSearch || !ingredient.industrial_product_id) searchProducts(productSearch);
                                }}
                                data-testid={`ingredient-search-${index}`}
                            />
                        </div>
                        {showProductDropdown && productResults.length > 0 && createPortal(
                            <div
                                data-dropdown-for={`ingredient-${index}`}
                                className="bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto"
                                style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex: 99999, pointerEvents: 'auto' }}
                            >
                                {productResults.map((p) => (
                                    <button
                                        key={p.id}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 border-b border-slate-50 last:border-0 cursor-pointer"
                                        onMouseDown={(e) => { e.preventDefault(); selectProduct(p); }}
                                        data-testid={`product-option-${p.id}`}
                                    >
                                        <span className="font-medium">{p.name}</span>
                                        {p.category && <span className="text-slate-400 ml-2 text-xs">{p.category}</span>}
                                        {p.supplier_products?.length > 0 && (
                                            <span className="text-slate-400 ml-2 text-xs">({p.supplier_products.length} fournisseur{p.supplier_products.length > 1 ? 's' : ''})</span>
                                        )}
                                    </button>
                                ))}
                            </div>,
                            getPortalTarget()
                        )}
                        {/* Supplier product info / select */}
                        {ingredient.industrial_product_id && ingredient._supplierProducts?.length > 1 && (
                            <div className="mt-1">
                                <Select
                                    value={ingredient.supplier_product_id || ''}
                                    onValueChange={(val) => {
                                        const sp = ingredient._supplierProducts.find(s => s.id === val);
                                        if (sp) selectSupplierProduct(sp);
                                    }}
                                >
                                    <SelectTrigger className="h-8 text-xs">
                                        <SelectValue placeholder="Choisir un fournisseur..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ingredient._supplierProducts.map((sp) => (
                                            <SelectItem key={sp.id} value={sp.id}>
                                                {sp.supplier_name_product} ({sp.supplier_name}) - {formatCurrency(sp.unit_price)}/{sp.invoice_unit || sp.order_unit || sp.billing_unit}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {ingredient.supplier_product_name && ingredient._supplierProducts?.length <= 1 && (
                            <p className="text-xs text-slate-400 mt-0.5 truncate">{ingredient.supplier_product_name}</p>
                        )}
                    </div>
                ) : (
                    <div>
                        <div ref={recipeInputRef}>
                            <Input
                                className="h-9 text-sm"
                                placeholder="Rechercher une recette..."
                                value={ingredient.sub_recipe_name || recipeSearch}
                                onChange={(e) => {
                                    setRecipeSearch(e.target.value);
                                    onUpdate(index, { ...ingredient, sub_recipe_name: '', sub_recipe_id: '' });
                                    searchRecipes(e.target.value);
                                }}
                                onFocus={() => {
                                    updateDropdownPos(recipeInputRef);
                                    if (recipeSearch || !ingredient.sub_recipe_id) searchRecipes(recipeSearch);
                                }}
                                data-testid={`ingredient-recipe-search-${index}`}
                            />
                        </div>
                        {showRecipeDropdown && recipeResults.length > 0 && createPortal(
                            <div
                                data-dropdown-for={`recipe-ingredient-${index}`}
                                className="bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto"
                                style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex: 99999, pointerEvents: 'auto' }}
                            >
                                {recipeResults.map((r) => (
                                    <button
                                        key={r.id}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-100 border-b border-slate-50 last:border-0 cursor-pointer"
                                        onMouseDown={(e) => { e.preventDefault(); selectRecipe(r); }}
                                        data-testid={`recipe-option-${r.id}`}
                                    >
                                        <span className="font-medium">{r.name}</span>
                                        <span className="text-slate-400 ml-2 text-xs">
                                            {formatCurrency(r.cost_per_portion)}/{pluralUnit(r.yield_unit || 'portion', r.portions)}
                                        </span>
                                    </button>
                                ))}
                            </div>,
                            getPortalTarget()
                        )}
                    </div>
                )}
            </td>

            {/* Quantity */}
            <td className="p-2 w-24 ing-cell" data-mobile-label="Quantité">
                <Input
                    type="number"
                    step="any"
                    min="0"
                    className="h-9 text-sm text-right"
                    value={ingredient._qtyInput ?? ingredient.quantity ?? ''}
                    onChange={(e) => {
                        const val = e.target.value;
                        const qty = val === '' ? 0 : (parseFloat(val) || 0);
                        onUpdate(index, {
                            ...ingredient,
                            quantity: qty,
                            _qtyInput: val,
                            total_cost: round(qty * (ingredient.unit_cost || 0)),
                        });
                    }}
                    data-testid={`ingredient-qty-${index}`}
                />
            </td>

            {/* Unit mode selector */}
            <td className="p-2 w-36 ing-cell" data-mobile-label="Unité">
                {ingredient.type === 'product' && ingredient.supplier_product_id ? (
                    <div className="space-y-1">
                        <Select
                            value={ingredient.use_base_unit ? 'base' : 'purchase'}
                            onValueChange={(v) => handleUnitModeChange(v === 'base')}
                        >
                            <SelectTrigger className="h-8 text-xs" data-testid={`ingredient-unit-mode-${index}`}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="purchase">{ingredient.purchase_unit || 'Achat'}</SelectItem>
                                {ingredient.base_unit && ingredient.base_unit !== ingredient.purchase_unit && (
                                    <SelectItem value="base">{ingredient.base_unit}</SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                        {ingredient.use_base_unit && (
                            <div>
                                <div className="flex items-center gap-1">
                                    <Input
                                        type="number"
                                        step="any"
                                        min="0"
                                        className={`h-7 text-xs w-16 text-right ${!ingredient.contenance ? 'border-amber-400 bg-amber-50' : ''}`}
                                        placeholder="?"
                                        value={ingredient._contInput ?? ingredient.contenance ?? ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const cont = val === '' ? 0 : (parseFloat(val) || 0);
                                            const rawPrice = ingredient._raw_unit_price || 0;
                                            const ppu = cont > 0 ? rawPrice / cont : 0;
                                            onUpdate(index, {
                                                ...ingredient,
                                                contenance: cont,
                                                _contInput: val,
                                                unit_cost: round(ppu),
                                                total_cost: round(ingredient.quantity * ppu),
                                            });
                                        }}
                                        data-testid={`ingredient-contenance-${index}`}
                                    />
                                    <span className="text-[10px] text-slate-400 whitespace-nowrap">{ingredient.base_unit}/{ingredient.purchase_unit}</span>
                                </div>
                                {!ingredient.contenance && (
                                    <p className="text-[10px] text-amber-600 mt-0.5">Requis pour le calcul</p>
                                )}
                            </div>
                        )}
                    </div>
                ) : ingredient.type === 'recipe' && ingredient.sub_recipe_id ? (
                    <Select
                        value={ingredient.unit || 'portion'}
                        onValueChange={(v) => onUpdate(index, { ...ingredient, unit: v })}
                    >
                        <SelectTrigger className="h-8 text-xs" data-testid={`ingredient-sub-unit-${index}`}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="portion">portion</SelectItem>
                            <SelectItem value="pièce">pièce</SelectItem>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="litre">litre</SelectItem>
                        </SelectContent>
                    </Select>
                ) : (
                    <span className="text-sm text-slate-500">{ingredient.unit || '-'}</span>
                )}
            </td>

            {/* Unit cost */}
            <td className="p-2 w-28 text-sm text-right text-slate-500 ing-cell" data-mobile-label="P.U.">
                {ingredient.unit_cost ? formatCurrency(ingredient.unit_cost) : '-'}
            </td>

            {/* Line cost */}
            <td className="p-2 w-28 text-sm text-right font-medium ing-cell" data-mobile-label="Coût">
                {ingredient.total_cost ? formatCurrency(ingredient.total_cost) : '-'}
            </td>

            {/* Reorder + Delete */}
            <td className="p-2 w-24 ing-cell ing-cell-actions" data-mobile-label="">
                <div className="flex items-center gap-0.5">
                    <div className="flex flex-col">
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 text-slate-400 hover:text-slate-700 disabled:opacity-20"
                            onClick={() => onMoveUp(index)}
                            disabled={isFirst}
                            data-testid={`ingredient-up-${index}`}
                        >
                            <ChevronUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 text-slate-400 hover:text-slate-700 disabled:opacity-20"
                            onClick={() => onMoveDown(index)}
                            disabled={isLast}
                            data-testid={`ingredient-down-${index}`}
                        >
                            <ChevronDown className="w-3.5 h-3.5" />
                        </Button>
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => onRemove(index)}
                        data-testid={`ingredient-remove-${index}`}
                    >
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </div>
            </td>
        </tr>
    );
}
